import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ChangeDetectionStrategy,
} from '@angular/core';

@Component({
  selector: 'lib-dnd-table',
  templateUrl: './dnd-table.component.html',
  styleUrls: ['./dnd-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DndTableComponent implements OnInit {
  @Input() dndEnabled = false;
  @Input() data: any[];
  @Input() hoverPointer = false;
  // The table scrolls sideways inside the component instead of the cells being squeezed past
  // readability, or the last columns falling off the edge with nothing to scroll.
  @Input() horizontalScroll = false;
  // Any CSS length: the width the columns need to stay readable, below which the container scrolls
  // instead of squeezing them further. Above it nothing changes - the columns share the extra space
  // the way an auto-layout table always has, so a wide screen keeps fully dynamic cells.
  @Input() minTableWidth?: string;
  // Any CSS length. Caps the scrolling area so the table scrolls in both directions inside the
  // viewport rather than the page scrolling, which keeps the horizontal bar at the bottom of the
  // visible area on a long table.
  @Input() maxHeight?: string;
  @Output() orderChanged = new EventEmitter<any[]>();
  @Output() rowClicked = new EventEmitter<any>();
  @ContentChild('rowTemplate', { read: TemplateRef })
  rowTemplate?: TemplateRef<any>;
  @ContentChild('headersTemplate', { read: TemplateRef })
  headersTemplate?: TemplateRef<any>;

  // Drives the scroll-mode rules in the stylesheet.
  @HostBinding('class.horizontal-scroll') get isHorizontalScroll(): boolean {
    return this.horizontalScroll;
  }

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  ngOnInit() {}

  handleRowClicked(el: any) {
    this.rowClicked.emit(el);
  }

  // The drag preview row is rendered out of the table flow, so its cells don't get the
  // table's computed column widths. Copy them from the placeholder row, which stays in place.
  dragStarted() {
    const host = this.elementRef.nativeElement;
    const placeholderCells = host.querySelectorAll<HTMLElement>('tr.cdk-drag-placeholder > td');
    const previewCells = host.querySelectorAll<HTMLElement>('tr.cdk-drag-preview > td');

    previewCells.forEach((previewCell, index) => {
      const placeholderCell = placeholderCells[index];
      if (placeholderCell) {
        previewCell.style.boxSizing = 'border-box';
        previewCell.style.width = `${placeholderCell.getBoundingClientRect().width}px`;
      }
    });
  }

  drop(event: CdkDragDrop<any>) {
    const previousIndex = this.data.findIndex((d) => d === event.item.data);

    moveItemInArray(this.data, previousIndex, event.currentIndex);
    this.orderChanged.emit(this.data);
  }
}
